






















































































































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { formatApiUrl, isEmptyOrWhiteSpace } from "@/utils/Common";
import { showConfirm, showError, showWarning } from "@/utils/UI";

import { API_BIS_IMPORT_PREVIEW, API_BIS_IMPORT_MAPPING, API_BIS_IMPORT_SUBMIT, API_BIS_UPLOAD_PATENT } from "@/configs/Apis";

declare interface ImportFieldMapping {
  entityField: string
  entityFieldLabel: string
  excelHeader: string
  primaryKey: boolean
}

declare interface FieldMapEntity {
  fieldMappingLists: Array<ImportFieldMapping>
  unMatchedHeaders: Array<string>
}

@Component
export default class ProjectImport extends Vue {
  @Prop({ required: true, type: Number })
  projectId!: number;

  @Prop({ type: Boolean, default: false })
  visible!: boolean;

  activeStep = 0;
  excelFile: Nullable<{ name: string, url: string }> = null;
  processing = false;
  importTaskId = "";
  preview = {
    columns: [""],
    data: []
  };

  mapping = {
    allExcelHeaders: [],
    data: []
  };

  duplicateStrategy = "SKIP";
  autoCreateEnum = true;
  autoCreateCategory = true;
  stepStatus = {
    uploaded: false,
    previewLoaded: false,
    mappingLoaded: false
  };

  get taskImportStatus() {
    let message = "";
    switch ((this.importData as any).status) {
      case "RUNNING":
        message = "导入运行中";
        break;
      case "COMPLETED":
        message = "导入完成";
        break;
      case "NOT_STARTED":
      default:
        message = "未开始";
        break;
    }
    return message;
  }

  get importData() {
    return this.$store.state.synctask.importTask;
  }

  get importPercentage() {
    const message = (this as any).importData;
    if (message.status === "COMPLETED") {
      return 100;
    }
    if (message.status === "NOT_STARTED") {
      return 0;
    }
    return parseInt((((message.finished + message.skipped) / (message.count || 1)) * 100).toString());
  }

  get uploadApi() {
    return `/api/${formatApiUrl(API_BIS_UPLOAD_PATENT, { dev: 1 })}`;
  }

  @Watch("activeStep")
  onActiveStep(step: number, oldStep: number) {
    if (step > oldStep) {
      const handler = (this as any)[`onStep${step}Load`];
      if (handler && Object.prototype.toString.call(handler) === "[object Function]") {
        handler.call(this);
      }
    }
    this.$emit("step-change", step);
  }

  @Watch("processing")
  onProcessing() {
    this.$emit("processing", this.processing);
  }

  validateFileType(file: File) {
    const ext = /\.\w+$/g.exec(file.name);
    if (!ext || [".xls", ".xlsx"].indexOf(ext[0].toLowerCase()) === -1) {
      showWarning("导入文件格式只能是Excel");
      return false;
    }
    return true;
  }

  onUploadChange(file: File) {
    if (this.excelFile && this.excelFile.name === file.name) {
      return;
    }
    this.excelFile = { name: file.name, url: "" } as any;
  }

  onRemoveFile() {
    this.excelFile = null;
    (this.$refs.uploader as any).uploadFiles = [];
    this.stepStatus.uploaded = false;
  }

  isEmpty(value: string) {
    return isEmptyOrWhiteSpace(value);
  }

  closeImportForm() {
    this.$emit("update:visible", false);
    this.$emit("close", ((this.importData || {}) as any).status);
  }

  doStep0() {
    const uploader = this.$refs.uploader as any;
    if (this.stepStatus.uploaded && !uploader.uploadFiles.length) {
      this.activeStep += 1;
      return;
    }
    if (!uploader.uploadFiles || !uploader.uploadFiles.length) {
      showWarning("请选择上传的文件");
      return;
    }
    this.processing = true;
    uploader.submit();
    this.stepStatus.mappingLoaded = false;
    this.stepStatus.previewLoaded = false;
  }

  doStep1() {
    this.activeStep += 1;
  }

  onStep1Load() {
    if (this.stepStatus.previewLoaded) {
      return;
    }
    this.processing = true;
    this.$http.post<{ header: Array<string>, data: Array<Array<any>> }>(API_BIS_IMPORT_PREVIEW, { fileUrl: this.excelFile?.url, projectId: this.projectId }).then(({ header, data } = { header: [], data: [] }) => {
      this.preview.columns = header as any;
      this.preview.data = data.map(item => item.reduce((prev, current, index) => {
        prev[`col${index}`] = current;
        return prev;
      }, {})) as any;
      this.stepStatus.previewLoaded = true;
    }).catch(() => "").then(() => {
      this.processing = false;
    });
  }

  onStep2Load() {
    if (this.stepStatus.mappingLoaded) {
      return;
    }
    this.processing = true;
    this.$http.post<FieldMapEntity>(API_BIS_IMPORT_MAPPING, { fileUrl: this.excelFile?.url, projectId: this.projectId }).then((entity = { fieldMappingLists: [], unMatchedHeaders: [] }) => {
      this.processing = false;
      this.mapping.data = entity.fieldMappingLists as any;
      const excelHeaders: Array<string> = [];
      entity.fieldMappingLists.forEach(field => {
        if (!isEmptyOrWhiteSpace(field.excelHeader)) {
          excelHeaders.push(field.excelHeader);
        }
      });
      Array.prototype.push.apply(excelHeaders, entity.unMatchedHeaders);
      this.mapping.allExcelHeaders = excelHeaders as any;
      this.stepStatus.mappingLoaded = true;
    }).catch(() => {
      this.processing = false;
    });
  }

  doStep2() {
    // 检查匹配字段
    const data: Array<ImportFieldMapping> = this.mapping.data;
    if (!this.mapping.data || !this.mapping.data.length) {
      showWarning("没有找到导入字段映射");
      return;
    }
    let hasUnMatched = false;
    for (let i = 0; i < data.length; i++) {
      if (data[i].primaryKey && isEmptyOrWhiteSpace(data[i].excelHeader)) {
        showWarning("主键字段没有匹配，不能导入");
        return;
      }
      if (isEmptyOrWhiteSpace(data[i].excelHeader)) {
        hasUnMatched = true;
        break;
      }
    }

    showConfirm(`${hasUnMatched ? "存在没有匹配的字段，" : ""}是否执行导入？`).then(() => {
      const postedParams = {
        projectId: this.projectId,
        fileUrl: this.excelFile?.url,
        duplicateStrategy: this.duplicateStrategy,
        autoCreateEnum: this.autoCreateEnum,
        autoCreateCategory: this.autoCreateCategory,
        fieldMappingList: JSON.parse(JSON.stringify(this.mapping.data))
      };
      this.$http.post<string>(API_BIS_IMPORT_SUBMIT, postedParams).then((taskId: string) => {
        this.importTaskId = taskId.replace(/"/g, "");
        this.activeStep += 1;
      });
    });
  }

  onStep3Load() {
    if (isEmptyOrWhiteSpace(this.importTaskId)) {
      return;
    }
    this.processing = true;
    this.$store.dispatch("synctask/progressImport", { taskId: this.importTaskId, projectId: this.projectId }).then(() => {
      this.processing = false;
    });
  }

  onUploadSuccess(res: { location: string }) {
    this.processing = false;
    this.stepStatus.uploaded = true;
    if (this.excelFile) {
      this.excelFile.url = res.location.replace(/"/g, "");
    }
    this.activeStep += 1;
  }

  onUploadError(...args: any[]) {
    showError(args[0]);
    this.processing = false;
  }

  doNextStep() {
    if (this.processing) {
      return;
    }
    const handler = (this as any)[`doStep${this.activeStep}`];
    if (handler && Object.prototype.toString.call(handler) === "[object Function]") {
      handler.call(this);
    }
  }

  doPrevStep() {
    if (this.activeStep === 0) {
      return;
    }
    this.activeStep -= 1;
  }
}
